import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useOnboardingCheck } from '@/hooks/useOnboardingCheck';

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { needsOnboarding, loading: onboardingLoading } = useOnboardingCheck();

  // Debounced loading state - only show spinner if loading takes > 200ms
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  // Extended loading message - only show if loading takes > 2 seconds
  const [showExtendedMessage, setShowExtendedMessage] = useState(false);
  
  useEffect(() => {
    console.log('[ProtectedRoute] Auth state:', { user: !!user, loading, onboardingLoading });
    if (loading || onboardingLoading) {
      const timer = setTimeout(() => {
        console.log('[ProtectedRoute] Showing loading spinner');
        setShowLoadingSpinner(true);
      }, 200);
      return () => clearTimeout(timer);
    } else {
      console.log('[ProtectedRoute] Hiding loading spinner');
      setShowLoadingSpinner(false);
    }
  }, [loading, onboardingLoading, user]);

  // Separate timer for extended message (2 seconds)
  useEffect(() => {
    if (loading || onboardingLoading) {
      const timer = setTimeout(() => {
        setShowExtendedMessage(true);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setShowExtendedMessage(false);
    }
  }, [loading, onboardingLoading]);

  // Failsafe: prevent infinite loading - after 3s, pass through if we have a user
  const [forceRender, setForceRender] = useState(false);
  useEffect(() => {
    if ((loading || onboardingLoading) && user) {
      const timer = setTimeout(() => {
        console.warn('[ProtectedRoute] Failsafe triggered - forcing render after 3s');
        setForceRender(true);
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      setForceRender(false);
    }
  }, [loading, onboardingLoading, user]);

  // Secondary failsafe: force render after 8s regardless of state
  const [hardForceRender, setHardForceRender] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading || onboardingLoading) {
        console.error('[ProtectedRoute] Hard failsafe triggered - forcing render after 8s');
        setHardForceRender(true);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [loading, onboardingLoading]);

  // If loading, show spinner only if it's taking time
  if ((loading || onboardingLoading) && !forceRender && !hardForceRender && showLoadingSpinner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
          {showExtendedMessage && (
            <p className="mt-2 text-xs text-muted-foreground/60">
              Taking longer than expected...
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!user && !loading && !onboardingLoading) {
    return <Navigate to="/login" replace />;
  }

  // If user needs onboarding and is NOT on the onboarding page, redirect them
  // This prevents access to /skin, /routine, /community, /home etc. without completing onboarding
  if (needsOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
